import React, { useEffect, useState } from "react";
import { adminApi } from "../apiAdmin";
import { fetchSupportedCountries, FALLBACK_COUNTRIES, SupportedCountry } from "../lib/supportedCountries";
import { NewTalentResult } from "../types/admin";

// Add Talent — admin-create flow for vetted talents (deferred-Stripe v1):
// the create form, the one-time credentials display it hands off to, and
// the resend-credentials confirmation that reuses that display. The
// password is shown ONCE and never stored anywhere admin can re-fetch.
// Moved out of pages/AdminDashboard.tsx in the Phase C split.

// Builds the message admin pastes into iMessage/text/DM after creating a talent.
// No emojis (per platform copy preferences). Plain text so it pastes cleanly.
const buildCredentialsMessage = (r: NewTalentResult) =>
  `I set up your Request App account.\n\n` +
  `Login: ${r.loginUrl}\n` +
  `Email: ${r.user.email}\n` +
  `Password: ${r.tempPassword}\n\n` +
  `Your QR code is already live — start sharing! Connect your bank when you want to cash out.`;

export function AdminAddTalentModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (result: NewTalentResult) => void;
}) {
  const [newTalent, setNewTalent] = useState({ name: "", bankCountryName: "", email: "" });
  // OPEN countries only — the server applies the same signup gate to
  // admin-created talent, so offering a locked country would just 403.
  const [signupCountries, setSignupCountries] = useState<SupportedCountry[]>(
    FALLBACK_COUNTRIES.filter((c) => c.open)
  );
  useEffect(() => {
    let alive = true;
    fetchSupportedCountries().then((list) => {
      if (alive) setSignupCountries(list.filter((c) => c.open));
    });
    return () => {
      alive = false;
    };
  }, []);
  const [newTalentError, setNewTalentError] = useState("");
  const [newTalentSubmitting, setNewTalentSubmitting] = useState(false);

  const handleCreateTalent = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewTalentError("");
    if (!newTalent.name.trim()) return setNewTalentError("Name is required");
    if (!newTalent.bankCountryName) return setNewTalentError("Country is required");

    setNewTalentSubmitting(true);
    try {
      const { data } = await adminApi.post<NewTalentResult>("/users", {
        name: newTalent.name.trim(),
        bankCountryName: newTalent.bankCountryName,
        email: newTalent.email.trim() || undefined,
      });
      onCreated(data);
    } catch (err: any) {
      setNewTalentError(err.response?.data?.msg || "Could not create talent");
    } finally {
      setNewTalentSubmitting(false);
    }
  };

  return (
    <div className="adm-modal-backdrop fade-in" onClick={() => !newTalentSubmitting && onClose()}>
      <div className="adm-modal-box" onClick={(e) => e.stopPropagation()}>
        <h3 className="adm-mb-4">Add Talent</h3>
        <p className="muted-text adm-mb-16 adm-fs-85">
          Creates an account with login credentials you can share. Talent can earn requests immediately — bank connection happens at first cashout.
        </p>
        <form onSubmit={handleCreateTalent} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label className="kpi-label adm-block adm-mb-4">
              Name *
            </label>
            <input
              className="adm-search adm-w-full"
              placeholder="DJ Mike, The Velvet Underground, Sarah K..."
              value={newTalent.name}
              onChange={(e) => setNewTalent({ ...newTalent, name: e.target.value })}
              autoFocus
              maxLength={100}
              required
            />
          </div>
          <div>
            <label className="kpi-label adm-block adm-mb-4">
              Country *
            </label>
            <select
              className="adm-select adm-w-full"
              value={newTalent.bankCountryName}
              onChange={(e) => setNewTalent({ ...newTalent, bankCountryName: e.target.value })}
              required
            >
              <option value="">Select country...</option>
              {signupCountries.map((c) => (
                <option key={c.code} value={c.name}>{c.flag}  {c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="kpi-label adm-block adm-mb-4">
              Email <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional — auto-generated if blank)</span>
            </label>
            <input
              className="adm-search adm-w-full"
              type="email"
              placeholder="Leave blank to auto-generate"
              value={newTalent.email}
              onChange={(e) => setNewTalent({ ...newTalent, email: e.target.value })}
            />
          </div>
          {newTalentError && (
            <div className="adm-red adm-fs-85">
              {newTalentError}
            </div>
          )}
          <div className="adm-modal-actions">
            <button
              type="button"
              className="adm-btn-ghost"
              onClick={onClose}
              disabled={newTalentSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="adm-btn-green"
              disabled={newTalentSubmitting}
            >
              {newTalentSubmitting ? "Creating..." : "Create Talent"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AdminCredentialsModal({ newTalentResult, onDone }: { newTalentResult: NewTalentResult; onDone: () => void }) {
  const [copyConfirm, setCopyConfirm] = useState(false);

  const copyCredentials = async () => {
    if (!newTalentResult) return;
    try {
      await navigator.clipboard.writeText(buildCredentialsMessage(newTalentResult));
      setCopyConfirm(true);
      setTimeout(() => setCopyConfirm(false), 2000);
    } catch {
      // Clipboard requires HTTPS / localhost. If denied, surface a message.
      alert("Could not copy automatically — please copy manually from the box above.");
    }
  };

  return (
    <div className="adm-modal-backdrop fade-in">
      <div className="adm-modal-box">
        <h3 className="adm-mb-4">Talent Created</h3>
        <p className="muted-text adm-mb-16 adm-fs-85">
          Save these credentials now. The password won&apos;t be shown again.
        </p>
        <div
          className="adm-p-14 adm-fs-85 adm-mb-12" style={{
            background: "#f3f4f6",
            borderRadius: 8,
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            color: "#111827",
            border: "1px solid #e5e7eb",
          }}
        >
          {buildCredentialsMessage(newTalentResult)}
        </div>
        <div className="adm-modal-actions">
          <button className="adm-btn-blue" onClick={copyCredentials}>
            {copyConfirm ? "Copied!" : "Copy All"}
          </button>
          <button className="adm-btn-ghost" onClick={onDone}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminResendCredentialsModal({
  resendCandidate,
  resendingPassword,
  onCancel,
  onConfirm,
}: {
  resendCandidate: { id: string; name: string };
  resendingPassword: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="adm-modal-backdrop fade-in"
      onClick={() => !resendingPassword && onCancel()}
    >
      <div className="adm-modal-box" onClick={(e) => e.stopPropagation()}>
        <h3 className="adm-mb-4">Resend Credentials</h3>
        <p className="muted-text adm-mb-16 adm-fs-85">
          Generate a new temporary password for{" "}
          <strong>{resendCandidate.name}</strong>? The talent&apos;s previous password
          stops working immediately.
        </p>
        <div className="adm-modal-actions">
          <button
            className="adm-btn-ghost"
            onClick={onCancel}
            disabled={resendingPassword}
          >
            Cancel
          </button>
          <button
            className="adm-btn-green"
            onClick={onConfirm}
            disabled={resendingPassword}
          >
            {resendingPassword ? "Generating..." : "Generate New Password"}
          </button>
        </div>
      </div>
    </div>
  );
}
